import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';

import AppSettingsService from 'services/appSettings';
import CancelModal from 'components/manageSessionModal/cancelModal';
import { toUkDisplayDate } from 'helpers/ukDate';
import { SECTION_AUDIENCE, SECTION_LIST } from 'constants/sections';

const OPTIONS = [
    { value: SECTION_AUDIENCE.ALL, label: 'Doctors and Admins' },
    { value: SECTION_AUDIENCE.SUPER_ADMIN, label: 'SuperAdmin only' },
];

/**
 * The server's rows, one per known section in our order; anything missing reads as superAdmin only.
 * A super-admin-only section (Statistics, Clinicians management, Staff activity) is a locked row: always
 * superAdmin, whatever the row says.
 */
function toRows(sections) {
    const byKey = {};
    (sections || []).forEach((row) => { byKey[row.section] = row; });

    return SECTION_LIST.map(({ key, label, superAdminOnly }) => {
        const row = byKey[key] || {};
        const locked = Boolean(superAdminOnly);

        return {
            key,
            label,
            locked,
            audience: !locked && row.audience === SECTION_AUDIENCE.ALL ? SECTION_AUDIENCE.ALL : SECTION_AUDIENCE.SUPER_ADMIN,
            updatedAt: row.updatedAt || null,
            updatedBy: row.updatedBy || null,
        };
    });
}

function lastChanged(row) {
    if (row.locked) {
        return 'Always super admins only';
    }
    if (!row.updatedAt) {
        return 'Not changed yet - super admins only';
    }

    return `Last changed${row.updatedBy ? ` by ${row.updatedBy.name}` : ''} on ${toUkDisplayDate(row.updatedAt)}`;
}

function openWarning(row) {
    return `Every Doctor and Admin will see ${row.label} in their menu and can open it.`;
}

/**
 * Which sections Doctors and Admins see (Activation codes, Vouchers). superAdmin only. 'all' opens a
 * section to Doctors (role 1) AND Admins (role 4) alike. Hide only: a "SuperAdmin only" section leaves
 * their menus and its URL bounces them. Opening a section asks first; limiting it again is the safe
 * direction and saves straight away.
 *
 * Statistics, Clinicians management and Staff activity are listed too, as locked rows: always super
 * admins only, their choices disabled, and no change is ever sent for them (the server would refuse one).
 */
export default function SectionAccessCard() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // { row, expected }: the section "Open to Doctors and Admins?" is about. Kept after the dialog
    // closes so its text does not change while it fades out.
    const [pendingOpen, setPendingOpen] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await AppSettingsService.getSectionAccess();
            setRows(toRows(data.sections));
            setError('');
        } catch (e) {
            setError('Could not load section access.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (section, audience, expectedAudience) => {
        setIsSaving(true);
        try {
            const { data } = await AppSettingsService.setSectionAccess(section, audience, expectedAudience);
            setRows(toRows(data.sections));
            setError('');
        } catch (e) {
            // The API's message (e.g. someone else changed it meanwhile) has already been shown;
            // show what is actually in force now.
            await load();
        } finally {
            setIsSaving(false);
        }
    };

    const choose = (row, audience) => {
        if (row.locked || isSaving || audience === row.audience) {
            return;
        }
        if (audience === SECTION_AUDIENCE.ALL) {
            setPendingOpen({ row, expected: row.audience });
            setIsConfirming(true);
            return;
        }
        save(row.key, audience, row.audience);
    };

    const confirmOpen = () => {
        if (!isConfirming || isSaving) {
            return;
        }
        setIsConfirming(false);
        save(pendingOpen.row.key, SECTION_AUDIENCE.ALL, pendingOpen.expected);
    };

    return (
        <Card className="card-auth overflow-hidden" data-testid="section-access-card">
            <CardBody>
                <label className="details-caption p-2">Section access</label>
                <div className="p-2">
                    <p className="text-muted font-size-12 mb-3">
                        Choose which sections Doctors and Admins see. Super admins always see every section.
                    </p>

                    {error ? <div className="text-danger mb-2" role="alert">{error}</div> : null}

                    {rows ? rows.map(row => (
                        <div key={row.key} className="mb-3" data-testid={`section-access-${row.key}`}>
                            <div className="font-weight-medium mb-1">{row.label}</div>
                            <div
                                className={`mh-options${row.locked ? ' mh-options--locked' : ''}`}
                                role="radiogroup"
                                aria-label={row.label}
                                aria-disabled={row.locked ? 'true' : undefined}
                            >
                                {OPTIONS.map(option => (
                                    <label
                                        key={option.value}
                                        className={`mh-option${row.audience === option.value ? ' mh-option--selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name={`sectionAccess-${row.key}`}
                                            value={option.value}
                                            checked={row.audience === option.value}
                                            disabled={isSaving || row.locked}
                                            onChange={() => choose(row, option.value)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="text-muted font-size-12 mt-1 mb-0" data-testid={`section-access-${row.key}-changed`}>
                                {lastChanged(row)}
                            </p>
                        </div>
                    )) : (!error && <div className="text-muted">Loading…</div>)}
                </div>
            </CardBody>

            <CancelModal
                visibleModal={isConfirming}
                setVisibleModal={setIsConfirming}
                handleClose={confirmOpen}
                header={pendingOpen ? `Open ${pendingOpen.row.label} to Doctors and Admins?` : ''}
                text={pendingOpen ? openWarning(pendingOpen.row) : ''}
                cancelButtonTitle="Cancel"
            />
        </Card>
    );
}
